import React, { useEffect, useMemo } from "react";

import { ArrowLeft, ChevronRight, Target } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import AlignmentCoverageSection from "./components/AlignmentCoverageSection";
import AssessmentsTrendsSection from "./components/AssessmentsTrendsSection";
import HeaderCommercialSection from "./components/HeaderCommercialSection";
import HealthSnapshotSection from "./components/HealthSnapshotSection";
import PublicDataSection from "./components/PublicDataSection";
import SynopsisSectionCard from "./components/SynopsisSectionCard";
import SynopsisTabsPanel from "./components/SynopsisTabsPanel";
import TeamsOverviewSection from "./components/TeamsOverviewSection";
import Button from "../../Components/Button";
import PortfolioAssessChoiceModal from "../../Container/AssessmentsContainer/PortfolioAssessChoiceModal";
import ReviewAssessmentModal from "../../Container/AssessmentsContainer/ReviewAssessmentModal";
import { useAccountSynopsisData } from "../../Hooks/useAccountSynopsisData";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchStakeholderRelationships } from "../../Store/Reducers/AccountDetailsSlice";
import { fetchAssessmentTemplates } from "../../Store/Reducers/AssessmentSlice";
import { toast } from "../../Utils/Helper/toast";
import {
  resolveSelfAssessmentTemplate,
  usePortfolioSelfAssessment,
} from "../MyPortfolioAccounts";
import AssessmentSubmitSuccessModal from "../ReviewAssessments/AssessmentSubmitSuccessModal";

type SynopsisLocationState = {
  from?: string;
  fromLabel?: string;
};

const AccountSynopsis: React.FC = () => {
  const { accountId, orgSlug } = useParams<{
    accountId: string;
    orgSlug?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const locationState = (location.state as SynopsisLocationState | null) ?? {};
  // Load vendor BH/AH bands on page mount — bubbles must not wait for legend tooltip open.
  useHealthScoreThresholds();

  const { templates: templatesPayload } = useAppSelector(
    state => state.assessment
  );

  const {
    accountSummary,
    summaryLoading,
    summaryError,
    briefLoading,
    publicBriefingAccount,
    publicSectionDots,
    publicSynopsisText,
    publicDataDate,
    overallScore,
    kpiDisplay,
    hawkeye,
    teamsLoading,
    customerMembers,
    customerLoading,
    accountTeamMembers,
    executiveSponsors,
    relationships,
    relationshipsLoading,
  } = useAccountSynopsisData(accountId);

  const withOrg = (path: string) => {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
  };

  const selfAssessmentTemplate = useMemo(
    () => resolveSelfAssessmentTemplate(templatesPayload),
    [templatesPayload]
  );

  const selfAssessment = usePortfolioSelfAssessment(
    selfAssessmentTemplate,
    withOrg
  );

  useEffect(() => {
    dispatch(fetchAssessmentTemplates({}));
  }, [dispatch]);

  const backPath = locationState.from ?? withOrg("/ops?tab=accounts");
  const backLabel = locationState.fromLabel ?? "Accounts";
  const accountName = accountSummary?.account_name?.trim();
  const pageTitle = accountName
    ? `${accountName} Synopsis`
    : "Account Synopsis";

  /** Trail matches entry point: Exec Ops > Accounts, or My Accounts, etc. */
  const fromOpsAccounts =
    backLabel === "Accounts" ||
    backPath.includes("/ops") ||
    !locationState.from;
  const breadcrumbTrail: { title: string; to?: string }[] = [
    ...(fromOpsAccounts ? [{ title: "Exec Ops", to: withOrg("/ops") }] : []),
    { title: backLabel, to: backPath },
    { title: "Account Synopsis" },
  ];

  const openAccountWorkspace = () => {
    if (!accountId) return;
    navigate(withOrg(`/account-workspace/${accountId}`), {
      state: {
        from: backPath,
        fromLabel: backLabel,
      },
    });
  };

  const openAssess = async () => {
    if (!accountId) return;
    const result = await selfAssessment.openForAccount(
      {
        id: accountId,
        name: accountSummary?.account_name || "Account",
      },
      { freshStart: true }
    );
    if (result === "finalization" || result === "esl_locked") {
      toast.info(
        "The due date has passed. You can view a submitted response, or there is no response submitted."
      );
    }
  };

  const openAlwaysOnChannel = () => {
    if (!accountId) {
      navigate(withOrg("/always-on-channel?briefingTab=temp-public"));
      return;
    }
    navigate(
      withOrg(
        `/always-on-channel?briefingTab=temp-public&accountId=${encodeURIComponent(accountId)}`
      )
    );
  };

  const openAlwaysOnChannelEnterpriseKpis = () => {
    if (!accountId) {
      navigate(withOrg("/always-on-channel?briefingTab=kpi"));
      return;
    }
    navigate(
      withOrg(
        `/always-on-channel?briefingTab=kpi&accountId=${encodeURIComponent(accountId)}`
      )
    );
  };

  const openAccountTeams = () => {
    if (!accountId) return;
    navigate(withOrg(`/account-workspace/${accountId}?tab=teams`));
  };

  const openRelationships = () => {
    if (!accountId) {
      navigate(withOrg("/relationship"));
      return;
    }
    navigate(
      withOrg(
        `/relationship?tab=account-relationships&accountId=${encodeURIComponent(accountId)}`
      )
    );
  };

  const refreshStakeholderPairs = () => {
    if (!accountId) return;
    dispatch(fetchStakeholderRelationships(accountId));
  };

  const openAssessments = () => {
    openRelationships();
  };

  // A signal card's "KPI detail" target links to #account-health; scroll
  // there once the section has its data.
  useEffect(() => {
    if (location.hash !== "#account-health" || kpiDisplay.loading) return;
    document
      .getElementById("account-health")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, kpiDisplay.loading]);

  return (
    <div className="mt-3 min-h-[calc(100vh-4rem)] w-full overflow-hidden bg-white">
      <div className="z-[1] border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 px-4 py-3">
          <Button
            variant="clear"
            onClick={() => navigate(backPath)}
            className="min-w-0 shrink-0 p-1"
            aria-label={`Back to ${backLabel}`}
          >
            <ArrowLeft size={15} color="#5F6368" />
          </Button>
          <h1 className="m-0 truncate text-xl font-bold leading-tight text-gray-900">
            {pageTitle}
          </h1>
        </div>

        <nav
          aria-label="Breadcrumb"
          className="border-t border-gray-100 bg-slate-50 px-4 py-2.5"
        >
          <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0 text-sm leading-5">
            {breadcrumbTrail.map((crumb, idx) => {
              const isLast = idx === breadcrumbTrail.length - 1;
              return (
                <React.Fragment key={`${crumb.title}-${idx}`}>
                  {idx > 0 ? (
                    <li
                      className="inline-flex items-center text-gray-400"
                      aria-hidden
                    >
                      <ChevronRight className="size-3.5 stroke-[2]" />
                    </li>
                  ) : null}
                  <li
                    className={
                      isLast
                        ? "font-semibold text-gray-900"
                        : "font-normal text-gray-500"
                    }
                    aria-current={isLast ? "page" : undefined}
                  >
                    {crumb.to && !isLast ? (
                      <Link
                        to={crumb.to}
                        className="font-normal text-gray-500 no-underline transition-colors hover:text-[#7C3AED]"
                      >
                        {crumb.title}
                      </Link>
                    ) : (
                      crumb.title
                    )}
                  </li>
                </React.Fragment>
              );
            })}
          </ol>
        </nav>
      </div>

      <div className="w-full bg-white p-3 md:p-5">
        <div className="flex w-full flex-col gap-5 rounded-xl bg-slate-100/90 p-4 md:p-5">
          {summaryError ? (
            <p
              className="m-0 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              role="alert"
            >
              {summaryError}
            </p>
          ) : null}

          {accountId ? (
            <>
              <HeaderCommercialSection
                account={accountSummary}
                loading={summaryLoading}
                onOpenWorkspace={openAccountWorkspace}
                onOpenAssess={openAssess}
                assessDisabled={
                  selfAssessment.isValidationLoading ||
                  selfAssessment.isSubmitting
                }
              />

              <PublicDataSection
                dots={publicSectionDots}
                synopsisText={publicSynopsisText}
                generatedDate={publicDataDate}
                loading={briefLoading}
                onOpenChannel={openAlwaysOnChannel}
                briefingAccount={publicBriefingAccount}
                organizationName={accountSummary?.account_name}
                accountId={accountId}
              />

              {/* Deep-link target for the "KPI detail" signal CTA target. */}
              <div id="account-health" className="scroll-mt-24">
                <HealthSnapshotSection
                  overallScore={overallScore}
                  categories={kpiDisplay.categories}
                  loading={kpiDisplay.loading}
                  onOpenChannel={openAlwaysOnChannelEnterpriseKpis}
                  accountMatrixData={hawkeye.matrixData[0] ?? null}
                  configuration={hawkeye.configuration}
                  orderedCategories={hawkeye.orderedCategories}
                  accountId={accountId}
                  organizationName={accountSummary?.account_name}
                />
              </div>

              <SynopsisSectionCard
                icon={Target}
                title="Alignment KPIs & Assessment Trends"
                linkLabel="Relationships"
                onLinkClick={openRelationships}
              >
                <AlignmentCoverageSection
                  assessmentLevel={accountSummary?.assessment_level}
                  executiveSponsors={executiveSponsors}
                  relationships={relationships}
                  loading={relationshipsLoading || customerLoading}
                  onOpenRelationships={openRelationships}
                  embedded
                />
                <div className="mt-5 border-t border-gray-200 pt-5">
                  <AssessmentsTrendsSection
                    accountId={accountId}
                    onOpenAssessments={openAssessments}
                    embedded
                  />
                </div>
              </SynopsisSectionCard>

              <TeamsOverviewSection
                customerMembers={customerMembers}
                customerLoading={customerLoading}
                accountTeamMembers={accountTeamMembers}
                executiveSponsors={executiveSponsors}
                vendorLoading={teamsLoading}
                onOpenTeams={openAccountTeams}
              />

              <SynopsisTabsPanel
                accountId={accountId}
                accountName={accountName || undefined}
                customerMembers={customerMembers}
                accountTeamMembers={accountTeamMembers}
                executiveSponsors={executiveSponsors}
                relationships={relationships}
                relationshipsLoading={relationshipsLoading}
                onOpenRelationships={openRelationships}
                onPairUpdated={refreshStakeholderPairs}
              />
            </>
          ) : null}
        </div>
      </div>

      <PortfolioAssessChoiceModal
        isOpen={selfAssessment.showChoiceModal}
        isLoading={
          selfAssessment.isValidationLoading && selfAssessment.showChoiceModal
        }
        accountName={selfAssessment.accountName}
        latest={selfAssessment.latestSelfData}
        startingKind={
          selfAssessment.takingKind === "formal" ? "formal" : "team"
        }
        onClose={selfAssessment.resetModal}
        onContinue={selfAssessment.continueFromChoice}
      />
      <AssessmentSubmitSuccessModal
        info={selfAssessment.submitSuccessInfo}
        onClose={selfAssessment.closeSuccessModal}
        onBackToStatus={() => {
          selfAssessment.closeSuccessModal();
          navigate(withOrg("/pending-assessments?tab=status"));
        }}
        onViewMix={info => {
          selfAssessment.closeSuccessModal();
          navigate(
            withOrg(
              `/pending-assessments?tab=level-matrix&mix=my&account=${info.accountId}`
            )
          );
        }}
      />
      <ReviewAssessmentModal
        isOpen={selfAssessment.showFormModal}
        templateData={selfAssessment.templateData}
        onClose={selfAssessment.resetModal}
        responses={selfAssessment.responses}
        onChange={selfAssessment.handleResponseChange}
        onSaveDraft={selfAssessment.saveDraft}
        onSubmit={selfAssessment.submit}
        isSubmitting={selfAssessment.isSubmitting}
        portfolioSubmitAction={selfAssessment.submitAction}
        voiceMode={selfAssessment.voiceMode}
        currentQuestionIndex={selfAssessment.questionIndex}
        onQuestionIndexChange={selfAssessment.setQuestionIndex}
        accountName={selfAssessment.accountName}
        footerVariant="portfolio"
        onGoToMyAssessments={selfAssessment.goToMyAssessments}
        canGoToMyAssessments={Boolean(selfAssessment.myAssessmentsNavTarget)}
        portfolioBlockedMessage={selfAssessment.formalAssessmentPendingMessage}
        isPortfolioValidationLoading={selfAssessment.isValidationLoading}
        onTakeFormalAssessment={
          selfAssessment.hasFormalAssessmentPending
            ? () => {
                void (async () => {
                  const opened =
                    await selfAssessment.takeBlockingFormalAssessment();
                  if (opened === true) return;
                  void selfAssessment.goToMyAssessments();
                })();
              }
            : undefined
        }
        isTakeFormalAssessmentLoading={selfAssessment.isTakeFormalLoading}
        portfolioAssessMode={selfAssessment.assessMode}
        portfolioFormFlow={selfAssessment.portfolioFormFlow}
        carryForwardNote={selfAssessment.carryForwardNote}
        onCarryForwardNoteChange={selfAssessment.setCarryForwardNote}
        onFormBack={selfAssessment.backToChoice}
        portfolioStatusBadge={selfAssessment.portfolioStatusBadge}
        isFormalFoldIn={selfAssessment.isFormalFoldIn}
        isDraftSession={selfAssessment.isDraftSession}
        forceReadOnly={selfAssessment.forceReadOnly}
        teamPeerBanner={
          selfAssessment.takingKind === "team"
            ? selfAssessment.teamPeerBanner
            : null
        }
        reassessBanner={
          selfAssessment.forceReadOnly || selfAssessment.isDraftSession
            ? null
            : selfAssessment.reassessBanner
        }
        remainderBanner={
          selfAssessment.takingKind === "team"
            ? selfAssessment.privateRemainder
            : null
        }
      />
    </div>
  );
};

export default AccountSynopsis;
